import React, { PropsWithChildren } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';
import { getThemeClassNames } from 'themes';

import { MirrorService } from 'services/MirrorService';
import { AppDomain } from 'types/enums/AppDomain';

import { Spinner, SpinnerTypes } from '../Spinner';

import css from './baseButton.module.sass';

export enum ButtonTypes {
  Primary,
  Accent,
  Contrast,
  Secondary,
  Transparent,
  Regular,
}

export type BaseButtonPropTypes = {
  loading?: boolean;
  nativeType?: 'submit' | 'button' | 'reset';
  disabled?: boolean;
  icon?: any;
  to?: any;
  type?: ButtonTypes;
  className?: string;
  ariaLabel?: string;
  small?: boolean;
  rounded?: boolean;
  autoHeight?: boolean;
  onClick?: (event?: any) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

const TYPES_WITH_DARK_SPINNER = [ButtonTypes.Contrast, ButtonTypes.Secondary];
const DOMAINS_WITHOUT_ACCENT_COLOR = [
  AppDomain.Meamo,
  AppDomain.Uniteus,
  AppDomain.FlirtGuru,
  AppDomain.Matchess,
  AppDomain.Ppl2ppl,
  AppDomain.Theunite,
  AppDomain.FinderSingles,
  AppDomain.JoyFinder,
  AppDomain.FlirtDiscovery,
  AppDomain.AdventureRelation,
  AppDomain.LocalsDate,
  AppDomain.LustStation,
  AppDomain.Localsjoy,
  AppDomain.MePlusYou,
  AppDomain.NeverAlones,
  AppDomain.DreamsDate,
  AppDomain.DateaSoul,
  AppDomain.DateaSoulmate,
  AppDomain.YourLove4Life,
  AppDomain.RelationshipNest,
  AppDomain.CupidKeys,
  AppDomain.AmourPulse,
  AppDomain.MyConnectly,
  AppDomain.TenderTalkers,
  AppDomain.SweatHeartSoul,
  AppDomain.Linkamora,
  AppDomain.Flirtiveo,
  AppDomain.Tendrilink,
  AppDomain.Chateluna,
  AppDomain.GetParamour,
  AppDomain.TripToLove,
  AppDomain.SupremeDate,
  AppDomain.TheCupies,
  AppDomain.PerfectDatings,
  AppDomain.DatingForLife,
  AppDomain.ForeverLover,
  AppDomain.FlirtingLover,
  AppDomain.DatingsLover,
  AppDomain.BeValentine,
  AppDomain.OnesValentine,
  AppDomain.DayDreamme,
  AppDomain.DayDreamsDate,
  AppDomain.LookLie,
  AppDomain.Delightedus,
  AppDomain.BestForFlirt,
  AppDomain.PerfectFlirt,
  AppDomain.MeetLie,
  AppDomain.KlubMe,
  AppDomain.Ppls,
  AppDomain.Hitchii,
  AppDomain.SociableSpots,
  AppDomain.MeetNests,
  AppDomain.PeopleMeetClub,
  AppDomain.GotLinkedNow,
  AppDomain.MeetHarbor,
  AppDomain.RelationTunes,
  AppDomain.DateMateHub,
  AppDomain.LinkupNook,
  AppDomain.TheLinkupers,
  AppDomain.SoulMatesLab,
  AppDomain.FairyDates,
  AppDomain.WaitForMe,
  AppDomain.LookFor4you,
  AppDomain.TheManWoman,
  AppDomain.UsTogether,
  AppDomain.AmorPassion,
  AppDomain.AmoreAffair,
  AppDomain.AmoreLover,
  AppDomain.AmorFlames,
  AppDomain.AmoreDesire,
  AppDomain.JustLovebirds,
  AppDomain.AdventureDate,
];

export const BaseButton: React.FC<PropsWithChildren<BaseButtonPropTypes>> = ({
  nativeType = 'button',
  loading = false,
  disabled = false,
  icon,
  to,
  type = ButtonTypes.Primary,
  className,
  ariaLabel,
  small,
  rounded,
  autoHeight,
  onClick,
  onMouseEnter,
  onMouseLeave,
  children,
}) => {
  const rootClassName = getThemeClassNames(css.root, {
    [AppDomain.MePlusYou]: css.rootRadius10,
    [AppDomain.Localsjoy]: css.rootRadius10,
    [AppDomain.Meamo]: css.rootRadius10,
    [AppDomain.Uniteus]: css.rootRadius10,
    [AppDomain.FlirtGuru]: css.rootRadius10,
    [AppDomain.Matchess]: css.rootRadius10,
    [AppDomain.Ppl2ppl]: css.rootRadius10,
    [AppDomain.Theunite]: css.rootRadius10,
    [AppDomain.FinderSingles]: css.rootRadius10,
    [AppDomain.JoyFinder]: css.rootRadius10,
    [AppDomain.FlirtDiscovery]: css.rootRadius10,
    [AppDomain.AdventureRelation]: css.rootRadius10,
    [AppDomain.LocalsDate]: `${css.rootRadius10} ${css.rootFontNormal}`,
    [AppDomain.DreamsDate]: `${css.fontTransformNormal} ${css.rootFontNormal}`,
    [AppDomain.Linkamora]: css.rootRadius10,
    [AppDomain.Flirtiveo]: css.rootRadius10,
    [AppDomain.Tendrilink]: css.rootRadius10,
    [AppDomain.Chateluna]: css.rootRadius10,
    [AppDomain.GetParamour]: css.rootRadius10,
    [AppDomain.TripToLove]: `${css.rootRadius10} ${css.rootFontNormal}`,
  });

  const primaryClassName = getThemeClassNames(css.btnPrimary, {
    [AppDomain.Meamo]: css.btnPrimaryColorBlack,
    [AppDomain.Uniteus]: css.btnPrimaryColorBlack,
    [AppDomain.FlirtGuru]: css.btnPrimaryColorBlack,
    [AppDomain.Flirtiveo]: css.btnPrimaryColorBlack,
    [AppDomain.AdventureDate]: css.btnPrimaryColorBlack,
  });

  const BUTTON_TYPE_CLASS_MAP = {
    [ButtonTypes.Primary]: primaryClassName,
    [ButtonTypes.Accent]: DOMAINS_WITHOUT_ACCENT_COLOR.includes(
      MirrorService.domain
    )
      ? primaryClassName
      : css.btnAccent,
    [ButtonTypes.Contrast]: css.btnContrast,
    [ButtonTypes.Secondary]: css.btnSecondary,
    [ButtonTypes.Transparent]: css.btnTransparent,
    [ButtonTypes.Regular]: css.btnRegular,
  };

  const buttonContent = icon ? (
    <span>
      <span className={css.icon}>{icon}</span>
      {children}
    </span>
  ) : (
    children
  );

  const classes = cx(
    rootClassName,
    [BUTTON_TYPE_CLASS_MAP[type]],
    {
      [css.disabled]: disabled || loading,
    },
    {
      [css.small]: small,
      [css.rounded]: rounded,
    },
    {
      [css.autoHeight]: autoHeight,
    },
    className
  );

  if (to) {
    return (
      <Link
        to={to}
        aria-label={ariaLabel}
        className={classes}
        onClick={
          disabled || loading
            ? (e) => {
                e.preventDefault();
              }
            : onClick
        }
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {loading ? (
          <Spinner
            type={
              TYPES_WITH_DARK_SPINNER.includes(type)
                ? SpinnerTypes.Dark
                : SpinnerTypes.Light
            }
          />
        ) : (
          buttonContent
        )}
      </Link>
    );
  }

  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={nativeType}
      disabled={disabled || loading}
      className={classes}
      aria-label={ariaLabel}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {loading ? (
        <Spinner
          type={
            TYPES_WITH_DARK_SPINNER.includes(type)
              ? SpinnerTypes.Dark
              : SpinnerTypes.Light
          }
        />
      ) : (
        buttonContent
      )}
    </button>
  );
};
